import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import useFetch from '../../hooks/useFetch';
import { Radar, Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns"; 
import Chart from "chart.js/auto"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { allSets as englishSets, horecaExtras as englishExtras } from "./horecaassessmentData";
import { allSets as hindiSets, horecaExtras as hindiExtras } from "./horecaassessmentDataHindi";


export default function HorecaHubSelfAssessment() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";
  const [saveMessage, setSaveMessage] = useState(""); 
  const [saveStatus, setSaveStatus] = useState("");   
  const [isSaved, setIsSaved] = useState(false);
  const [isHistoryFetched, setIsHistoryFetched] = useState(false);    

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lineChartRef = useRef(null);
  const [language, setLanguage] = React.useState("en");
  const allSets = language === "en" ? englishSets : hindiSets;
  const horecaExtras = language === "en" ? englishExtras : hindiExtras;
  const [aiPrompt, setAiPrompt] = useState("");
  const [notification, setNotification] = useState(""); 
  const [notificationType, setNotificationType] = useState("info"); 
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSet, setPendingSet] = useState(null);

  const [fetchData, { loading }] = useFetch();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [selectedSet, setSelectedSet] = useState(1);
  const [activeTab, setActiveTab] = useState("start");
  const baseQuestions = allSets[selectedSet];

  const handleSave = async () => {
    const formData = {
      answers,
      horecaAnswers,
      selectedSet,
      includeHoreca,
      score,
      level: levelObj.name,
      recs,
      userCode: authState.user.think5whyscode, 
    };

    const config = {
      url: "/msmeselfassessment",   
      method: "post",
      data: {
        formData,
        user: authState.user._id,
        timestamp: new Date().toISOString(),
      },
      headers: { Authorization: authState.token },
    };

    try {
      const result = await fetchData(config);

      if (result?.success) {
        setSaveMessage("Saved successfully!");
        setSaveStatus("success");
        setIsSaved(true);
      } else {
        setSaveMessage("Error saving data");
        setSaveStatus("error");
      }
    } catch (err) {
      console.error("Save failed", err);
      setSaveMessage("Something went wrong!");
      setSaveStatus("error");
    }

    setTimeout(() => {
      setSaveMessage("");
      setSaveStatus("");
    }, 3000);
  };

  const handleShare = () => {
    const textToShare = `Self Assessment – Set ${selectedSet}\nScore: ${score} / ${maxScore()}\nStage: ${levelObj.name}\nRecommendations:\n${recs.join("\n")}`;

    if (navigator.share) {
      // Mobile native share
      navigator
        .share({
          title: `Assessment Set ${selectedSet}`,
          text: textToShare,
        })
        .then(() => console.log("Shared successfully"))
        .catch((err) => console.error("Share failed:", err));
    } else {
      // Fallback: WhatsApp share
      const whatsappURL = `https://api.whatsapp.com/send?text=${encodeURIComponent(textToShare)}`;
      window.open(whatsappURL, "_blank");
    }
  };

  const handleGeneratePrompt = () => {
    const allAnswers = baseQuestions.map((q) => {
      const ans = answers[q.id] || "No answer";
      return `Q: ${q.label}\nA: ${ans}`;
    });

    if (includeHoreca) {
      horecaExtras.forEach((q) => {
        const ans = horecaAnswers[q.id] || "No answer";
        allAnswers.push(`Q: ${q.label}\nA: ${ans}`);
      });
    }

    const prompt = `
    You are a business consultant AI. Based on the following Self Assessment:

    Set: ${selectedSet}
    Total Score: ${score} / ${maxScore()}
    Stage: ${levelObj.name}
    Recommendations: ${recs.join(", ")}

    User Answers:
    ${allAnswers.join("\n")}

    Generate 10 creative, actionable, and practical Call-to-Actions (CTA) that the user can implement in their business. 
    `;

    setAiPrompt(prompt);
    setNotification("AI Prompt generated! You can copy it from below.");
    setNotificationType("success");
  };

  const handleAskThinkyAI = () => {
    if (!aiPrompt) {
      setNotification("Please generate AI Prompt first!");
      setNotificationType("error");
      return;
    }    
    navigate("/agenticsim", { state: { prompt: aiPrompt } });
  };

  const handleCTA = () => {
    navigate("/tasks/add");
  };

  const dummyHistory = [
    // Set 1 (original 10 entries)
    { id: 1, set_id: 1, date: "2025-02-01 10:30 AM", level: "Beginner", difficulty_level: "Easy", score: 18, total: 20, percent: 90 },
    { id: 2, set_id: 1, date: "2025-02-05 02:15 PM", level: "Intermediate", difficulty_level: "Medium", score: 22, total: 25, percent: 88 },
    { id: 3, set_id: 1, date: "2025-02-09 04:12 PM", level: "Advanced", difficulty_level: "Hard", score: 26, total: 30, percent: 86 },
    { id: 4, set_id: 1, date: "2025-02-12 11:45 AM", level: "Beginner", difficulty_level: "Easy", score: 19, total: 20, percent: 95 },
    { id: 5, set_id: 1, date: "2025-02-15 09:20 AM", level: "Intermediate", difficulty_level: "Medium", score: 23, total: 25, percent: 92 },
    { id: 6, set_id: 1, date: "2025-02-18 06:05 PM", level: "Advanced", difficulty_level: "Hard", score: 27, total: 30, percent: 90 },
    { id: 7, set_id: 1, date: "2025-02-21 07:50 PM", level: "Beginner", difficulty_level: "Easy", score: 20, total: 20, percent: 100 },
    { id: 8, set_id: 1, date: "2025-02-25 01:10 PM", level: "Intermediate", difficulty_level: "Medium", score: 21, total: 25, percent: 84 },
    { id: 9, set_id: 1, date: "2025-02-26 08:30 AM", level: "Advanced", difficulty_level: "Hard", score: 25, total: 30, percent: 83 },
    { id: 10, set_id: 1, date: "2025-02-27 03:55 PM", level: "Intermediate", difficulty_level: "Medium", score: 24, total: 25, percent: 96 },

    // Set 2 (5 entries)
    { id: 11, set_id: 2, date: "2025-02-03 10:00 AM", level: "Beginner", difficulty_level: "Easy", score: 17, total: 20, percent: 85 },
    { id: 12, set_id: 2, date: "2025-02-08 01:30 PM", level: "Intermediate", difficulty_level: "Medium", score: 20, total: 25, percent: 80 },
    { id: 13, set_id: 2, date: "2025-02-10 03:15 PM", level: "Advanced", difficulty_level: "Hard", score: 23, total: 30, percent: 77 },
    { id: 14, set_id: 2, date: "2025-02-12 11:00 AM", level: "Beginner", difficulty_level: "Easy", score: 18, total: 20, percent: 90 },
    { id: 15, set_id: 2, date: "2025-02-15 02:45 PM", level: "Intermediate", difficulty_level: "Medium", score: 21, total: 25, percent: 84 },

    // Set 3 (5 entries)
    { id: 16, set_id: 3, date: "2025-02-02 09:30 AM", level: "Beginner", difficulty_level: "Easy", score: 19, total: 20, percent: 95 },
    { id: 17, set_id: 3, date: "2025-02-08 12:20 PM", level: "Intermediate", difficulty_level: "Medium", score: 22, total: 25, percent: 88 },
    { id: 18, set_id: 3, date: "2025-02-22 04:10 PM", level: "Advanced", difficulty_level: "Hard", score: 24, total: 30, percent: 80 },
    { id: 19, set_id: 3, date: "2025-02-25 11:50 AM", level: "Beginner", difficulty_level: "Easy", score: 20, total: 20, percent: 100 },
    { id: 20, set_id: 3, date: "2025-02-28 03:30 PM", level: "Intermediate", difficulty_level: "Medium", score: 21, total: 25, percent: 84 },

    // Set 4 (5 entries)
    { id: 21, set_id: 4, date: "2025-02-06 10:15 AM", level: "Beginner", difficulty_level: "Easy", score: 18, total: 20, percent: 90 },
    { id: 22, set_id: 4, date: "2025-02-07 02:05 PM", level: "Intermediate", difficulty_level: "Medium", score: 21, total: 25, percent: 84 },
    { id: 23, set_id: 4, date: "2025-02-11 04:45 PM", level: "Advanced", difficulty_level: "Hard", score: 25, total: 30, percent: 83 },
    { id: 24, set_id: 4, date: "2025-02-17 11:25 AM", level: "Beginner", difficulty_level: "Easy", score: 19, total: 20, percent: 95 },
    { id: 25, set_id: 4, date: "2025-02-28 03:50 PM", level: "Intermediate", difficulty_level: "Medium", score: 22, total: 25, percent: 88 },

    // Set 5 (5 entries)
    { id: 26, set_id: 5, date: "2025-02-12 09:40 AM", level: "Beginner", difficulty_level: "Easy", score: 20, total: 20, percent: 100 },
    { id: 27, set_id: 5, date: "2025-02-15 01:30 PM", level: "Intermediate", difficulty_level: "Medium", score: 22, total: 25, percent: 88 },
    { id: 28, set_id: 5, date: "2025-02-18 03:15 PM", level: "Advanced", difficulty_level: "Hard", score: 26, total: 30, percent: 87 },
    { id: 29, set_id: 5, date: "2025-02-20 11:45 AM", level: "Beginner", difficulty_level: "Easy", score: 19, total: 20, percent: 95 },
    { id: 30, set_id: 5, date: "2025-02-22 02:55 PM", level: "Intermediate", difficulty_level: "Medium", score: 23, total: 25, percent: 92 },
  ];

  const filteredHistory = dummyHistory
    .filter((h) => h.set_id === selectedSet)
    .map((h) => {
      const [datePart, timePart, meridiem] = h.date.split(" ");
      let [hour, minute] = timePart.split(":").map(Number);
      if (meridiem === "PM" && hour < 12) hour += 12;
      if (meridiem === "AM" && hour === 12) hour = 0;
      const isoDate = `${datePart}T${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}:00`;
      return { ...h, isoDate: new Date(isoDate) }; 
    });

  const trendData = {
    labels: filteredHistory.map((h) => h.isoDate),
    datasets: [
      {
        label: `Set ${selectedSet} Performance (%)`,
        data: filteredHistory.map((h) => h.percent),
        borderColor: "rgba(250, 204, 21, 1)",
        backgroundColor: "rgba(250, 204, 21, 0.3)",
        borderWidth: 3,
        tension: 0.3,
        pointRadius: 4,
      },
    ],
  };

  const trendOptions = {
    responsive: true,
    plugins: { 
      legend: { labels: { color: "white" } },
      title: {
        display: true,
        text: `Performance Trend for Set ${selectedSet}`,
        color: "white",
      },
    },
    scales: {
      x: {
        type: "time",
        time: { unit: "day", tooltipFormat: "MMM dd, yyyy HH:mm" },
        ticks: { color: "white" },
        grid: { color: "#333" },
      },
      y: {
        min: 0,
        max: 100,
        ticks: { color: "white" },
        grid: { color: "#333" },
      },
    },
  };

  const initialAnswers = {};
  baseQuestions.forEach((q) => (initialAnswers[q.id] = 1));

  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem("horecahub_assessment_answers");
      return saved ? JSON.parse(saved) : initialAnswers;
    } catch (e) {
      return initialAnswers;
    }
  });

  const [includeHoreca, setIncludeHoreca] = useState(false);
  const [horecaAnswers, setHorecaAnswers] = useState(() => {
    const ia = {};
    horecaExtras.forEach((q) => (ia[q.id] = 1));
    return ia;
  });

  useEffect(() => {
    localStorage.setItem("horecahub_assessment_answers", JSON.stringify(answers));
  }, [answers]);

  const handleChange = (id, value, isHoreca = false) => {
    if (isHoreca) {
      setHorecaAnswers((s) => ({ ...s, [id]: Number(value) }));
    } else {
      setAnswers((s) => ({ ...s, [id]: Number(value) }));
    }
  };

  const totalScore = () => {
    const baseTotal = Object.values(answers).reduce((a, b) => a + b, 0);
    const ag = includeHoreca ? Object.values(horecaAnswers).reduce((a, b) => a + b, 0) : 0;
    return baseTotal + ag;
  };

  const maxScore = () => {
    const baseMax = baseQuestions.length * 5;
    const ag = includeHoreca ? horecaExtras.length * 5 : 0;
    return baseMax + ag;
  };

  const levelFromScore = (score) => {
    const max = maxScore();
    const scaled = Math.round((score / max) * 75);
    if (scaled <= 25) return { level: 1, name: "Level 1 – Ad-hoc / Manual" };
    if (scaled <= 40) return { level: 2, name: "Level 2 – Emerging / Functional" };
    if (scaled <= 55) return { level: 3, name: "Level 3 – Process-Driven" };
    if (scaled <= 65) return { level: 4, name: "Level 4 – Integrated & Quality-Focused" };
    return { level: 5, name: "Level 5 – Predictive & Scalable" };
  };

  const getRecommendations = (level) => {
    const map = {
      1: [
        "Document core processes as simple SOPs or checklists.",
        "Start digital record-keeping (mobile spreadsheet or simple app).",
        "Train staff on basic hygiene and consistency (5S checklists).",
      ],
      2: [
        "Define SOPs for all recurring activities and assign process owners.",
        "Introduce basic KPIs and simple ERP/accounting integration.",
        "Begin quality checks in-process rather than only at the end.",
      ],
      3: [
        "Run internal process audits and implement corrective action loops.",
        "Pursue relevant quality certifications (ISO 9001, HACCP, ZED).",
        "Use data to identify recurring defects and reduce waste.",
      ],
      4: [
        "Integrate supply-chain partners into your workflows (shared dashboards).",
        "Implement sustainability metrics and report monthly.",
        "Use dashboards for KPI review and continuous improvement cycles.",
      ],
      5: [
        "Adopt predictive analytics for demand & quality forecasting.",
        "Build R&D or innovation partnerships and export readiness.",
        "Lead cluster-level data-sharing and shared infrastructure (labs, cold chain).",
      ],
    };
    return map[level] || [];
  };

  const pillarScores = () => {
    const pillarMap = {};
    baseQuestions.forEach((q) => {
      pillarMap[q.pillar] = (pillarMap[q.pillar] || 0) + answers[q.id];
    });
    if (includeHoreca) {
      horecaExtras.forEach((q) => {
        pillarMap[q.pillar] = (pillarMap[q.pillar] || 0) + horecaAnswers[q.id];
      });
    }

    const pillarCounts = {};
    baseQuestions.forEach((q) => (pillarCounts[q.pillar] = (pillarCounts[q.pillar] || 0) + 1));
    if (includeHoreca) horecaExtras.forEach((q) => (pillarCounts[q.pillar] = (pillarCounts[q.pillar] || 0) + 1));

    const data = Object.keys(pillarMap).map((p) => ({
      subject: p,
      A: +(pillarMap[p] / pillarCounts[p]).toFixed(2),
      fullMark: 5,
    }));
    return data;
  };

  const resetAll = () => {
    const ia = {};
    baseQuestions.forEach((q) => (ia[q.id] = 1));
    setAnswers(ia);
    const aa = {};
    horecaExtras.forEach((q) => (aa[q.id] = 1));
    setHorecaAnswers(aa);
    setIncludeHoreca(false);
    localStorage.removeItem("horecahub_assessment_answers");
  };

  const exportJSON = () => {
    const payload = {
      answers,
      horecaAnswers: includeHoreca ? horecaAnswers : null,
      total: totalScore(),
      max: maxScore(),
      level: levelFromScore(totalScore()),
      date: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "horecahub_self_assessment.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const score = totalScore();
  const levelObj = levelFromScore(score);
  const recs = getRecommendations(levelObj.level);
  const chartData = pillarScores();

  const labels = chartData.map((d) => d.subject);
  const dataPoints = chartData.map((d) => d.A);

  const radarData = {
    labels,
    datasets: [
      {
        label: "Average Score",
        data: dataPoints,
        backgroundColor: "rgba(16, 185, 129, 0.2)",
        borderColor: "#10b981",
        borderWidth: 2,
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#10b981",
        pointHoverBackgroundColor: "#10b981",
        pointHoverBorderColor: "#10b981",
      },
    ],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: { stepSize: 1, backdropColor: "transparent" },
        grid: { color: "rgba(0,0,0,0.1)" },
        pointLabels: { font: { size: 12 } },
        angleLines: { color: "rgba(0,0,0,0.1)" },
      },
    },
    plugins: { legend: { display: true, position: "top" } },
  };

const chartRefs = {
  1: useRef(null),
  2: useRef(null),
  3: useRef(null),
  4: useRef(null),
  5: useRef(null),
};

// const buildRadarDataForSet = (setId) => {
//   return {
//     labels: radarData.labels, 
//     datasets: [
//       {
//         ...radarData.datasets[0],
//         label: `Set ${setId}`,
//         data: getPillarScoresForSet(setId), 
//       },
//     ],
//   };
// };

const downloadAllSheetsAsPDF = async () => {
  const doc = new jsPDF("p", "mm", "a4");

  for (let setId = 1; setId <= 5; setId++) {
    const element = document.getElementById(`pdfSheet${setId}`);
    if (!element) continue;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png", 1.0);

    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    doc.addImage(imgData, "PNG", 0, 10, pdfWidth, pdfHeight);

    if (setId < 5) doc.addPage();
  }

  doc.save("SelfAssessment_All_Sets.pdf");
};

const printHistoryForSet = (setId) => {
  const rows = dummyHistory.filter((h) => h.set_id === setId);

  const html = `
    <html>
      <head>
        <title>Assessment History – Set ${setId}</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          h2 { color: #1e40af; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #999; padding: 8px; text-align: left; }
          th { background: #facc15; }
        </style>
      </head>
      <body>
        <h2>HorecaHub Self Assessment – Set ${setId} History</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Level</th>
              <th>Difficulty</th>
              <th>Score</th>
              <th>Total</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (r) => `
                <tr>
                  <td>${r.date}</td>
                  <td>${r.level}</td>
                  <td>${r.difficulty_level}</td>
                  <td>${r.score}</td>
                  <td>${r.total}</td>
                  <td>${r.percent}%</td>
                </tr>
              `
              )
              .join("")}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.print();
};

const printAllHistory = () => {
    const html = `
      <html>
        <head>
          <title>HorecaHub Assessment – Full History</title>
          <style>
            body { font-family: Arial; padding: 20px; }
            h2 { color: #1e40af; margin-top: 30px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            th, td { border: 1px solid #999; padding: 6px; }
            th { background: #facc15; }
          </style>
        </head>
        <body>
          <h1>HorecaHub Self Assessment – Complete History</h1>
          ${[1, 2, 3, 4, 5]
            .map((setId) => {
              const rows = dummyHistory.filter((h) => h.set_id === setId);
              if (!rows.length) return "";

              return `
                <h2>Set ${setId}</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Level</th>
                      <th>Difficulty</th>
                      <th>Score</th>
                      <th>Total</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${rows
                      .map(
                        (r) => `
                          <tr>
                            <td>${r.date}</td>
                            <td>${r.level}</td>
                            <td>${r.difficulty_level}</td>
                            <td>${r.score}</td>
                            <td>${r.total}</td>
                            <td>${r.percent}%</td>
                          </tr>
                        `
                      )
                      .join("")}
                  </tbody>
                </table>
              `;
            })
            .join("")}
        </body>
      </html>
    `;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.print();
  };

const printTrendForSet = () => {
  if (!lineChartRef.current) return;

  const chartInstance = lineChartRef.current;
  const chartImage = chartInstance.toBase64Image();

  const html = `
    <html>
      <head>
        <title>Performance Trend – Set ${selectedSet}</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          h2 { color: #1e40af; margin-bottom: 20px; }
          img { width: 100%; max-width: 800px; }
        </style>
      </head>
      <body>
        <h2>Performance Trend – Set ${selectedSet}</h2>
        <p>Based on assessment percentage over time</p>
        <img src="${chartImage}" alt="Trend Chart" />
        <script>
          window.onload = function () {
            window.print();
          };
        </script>
      </body>
    </html>
  `;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
};

// const printAllTrends = () => {
//   const charts = [];

//   for (let setId = 1; setId <= 5; setId++) {
//     const setHistory = dummyHistory.filter((h) => h.set_id === setId);
//     const data = {
//       labels: setHistory.map((h) => h.isoDate || h.date),
//       datasets: [
//         {
//           label: `Set ${setId} Performance (%)`,
//           data: setHistory.map((h) => h.percent),
//           borderColor: "rgba(250, 204, 21, 1)",
//           backgroundColor: "rgba(250, 204, 21, 0.3)",
//           borderWidth: 3,
//           tension: 0.3,
//           pointRadius: 4,
//         },
//       ],
//     };

//     // Create temporary canvas
//     const canvas = document.createElement("canvas");
//     canvas.width = 800; 
//     canvas.height = 400;
//     document.body.appendChild(canvas); 

//     const tempChart = new ChartJS.Chart(canvas, {
//       type: "line",
//       data,
//       options: trendOptions,
//     });

//     // Convert to image
//     charts.push({ setId, imgData: tempChart.toBase64Image() });

//     // Cleanup
//     tempChart.destroy();
//     document.body.removeChild(canvas);
//   }

//   // Build printable HTML
//   const html = `
//     <html>
//       <head>
//         <title>All Trends – Sets 1–5</title>
//         <style>
//           body { font-family: Arial; padding: 20px; }
//           h1 { color: #1e40af; margin-bottom: 30px; }
//           h2 { color: #1e40af; margin-top: 40px; margin-bottom: 10px; }
//           img { width: 100%; margin-bottom: 40px; }
//         </style>
//       </head>
//       <body>
//         <h1>Performance Trends – Sets 1–5</h1>
//         ${charts.map(c => `<h2>Set ${c.setId}</h2><img src="${c.imgData}" />`).join("")}
//         <script>window.onload = function(){ window.print(); }</script>
//       </body>
//     </html>
//   `;

//   const win = window.open("", "_blank");
//   win.document.write(html);
//   win.document.close();
// };

const printAllTrendsAsPDF = async () => {
  const doc = new jsPDF("l", "pt", "a4"); // landscape
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 40;

  for (let setId = 1; setId <= 5; setId++) {
    const setHistory = dummyHistory.filter((h) => h.set_id === setId);

    if (setHistory.length === 0) continue;

    const data = {
      labels: setHistory.map((h) => h.date),
      datasets: [
        {
          label: `Set ${setId} Performance (%)`,
          data: setHistory.map((h) => h.percent),
          borderColor: "rgba(250, 204, 21, 1)",
          backgroundColor: "rgba(250, 204, 21, 0.3)",
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 4,
        },
      ],
    };

    const canvas = document.createElement("canvas");
    canvas.width = 800;
    canvas.height = 400;
    document.body.appendChild(canvas);

    await new Promise((resolve) => {
      new Chart(canvas, {
        type: "radar",
        data: radarData,
        options: {
          ...radarOptions,
          animation: {
            onComplete: () => resolve(), 
          },
        },
      });
    });

    const img = canvas.toDataURL("image/png");
    await new Promise((res) => setTimeout(res, 100));
    const imgData = tempChart.toBase64Image();
    doc.setFontSize(16);
    doc.text(`Performance Trend – Set ${setId}`, 40, yPosition);
    doc.addImage(imgData, "PNG", 40, yPosition + 10, pageWidth - 80, 200);

    yPosition += 220;
    if (yPosition + 220 > pageHeight) {
      doc.addPage();
      yPosition = 40;
    }

    tempChart.destroy();
    document.body.removeChild(canvas);
  }

  doc.save("All_Trends_Sets_1-5.pdf");
};

  return (
    <div className="bg-black">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-m lg:text-xl font-bold text-yellow-200">HorecaHub Process Maturity — Self Assessment</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="mt-4 p-2 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-3 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                Login
              </button>
            </>
          )}
        </div>
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm font-medium">{userName}</div>
              <div className="flex gap-3">
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                >
                  ⏻
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="text-teal-300">Login</Link>
            </div>
          )}
        </div>
      )}
      
      <div className="bg-black p-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-6 text-sm place-items-center border border-blue-200 p-2">
          <button
            onClick={() => setActiveTab("start")}
            className={`py-2 rounded text-white border ${
              activeTab === "start" ? "bg-blue-600 border-blue-400" : "bg-slate-800 border-slate-600"
            }`}
          >
            Start Assessment
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`py-2 rounded text-white border ${
              activeTab === "history" ? "bg-blue-600 border-blue-400" : "bg-slate-800 border-slate-600"
            }`}
          >
            History
          </button>
          <button
            onClick={() => setActiveTab("trend")}
            className={`py-2 rounded text-white border ${
              activeTab === "trend" ? "bg-blue-600 border-blue-400" : "bg-slate-800 border-slate-600"
            }`}
          >
            Trend
          </button>
          <select
            value={language} 
            onChange={(e) => setLanguage(e.target.value)}
            className="h-10 py-2 px-2 rounded text-sm bg-slate-800 text-white border border-slate-600 focus:outline-none"
          >
            <option value="en">English</option>
            <option value="hi">Hindi</option>
          </select>
        </div>

        {activeTab === "start" && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">             
              <div className="md:flex md:flex-col md:gap-1">
                <h1 className="text-start text-lg lg:text-xl font-bold text-yellow-200 mb-1">
                  Self Assessment Form
                </h1>
                <p className="text-sm text-white">
                  Complete the 1–5 scale for each item and view your maturity level.
                </p>
              </div>

              <button
                onClick={downloadAllSheetsAsPDF}
                className="text-xs lg:text-sm w-36 lg:w-48 mt-3 md:mt-0 px-4 py-2 rounded border border-yellow-400 text-yellow-300 bg-black hover:bg-yellow-400 hover:text-black flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faFilePdf}/>Download All Sheets
              </button>

            </div>
            <div className="grid grid-cols-5 gap-2 mb-2 place-items-center bg-gray-800 py-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    if (n === selectedSet) return; 
                    setPendingSet(n);
                    setShowConfirmModal(true);
                  }}
                  className={`px-3 py-1 rounded border text-xs lg:text-sm ${
                    selectedSet === n
                      ? "bg-blue-600 text-white border-blue-400"
                      : "bg-slate-800 text-white border-slate-600"
                  }`}
                >
                  Set {n}
                </button>
              ))}
            </div>

            {showConfirmModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-gray-900 text-white p-6 rounded-lg max-w-sm w-full shadow-lg">
                  <h2 className="text-lg font-bold mb-2">Change Set?</h2>
                  <p className="mb-4">
                    Changing the set will **reset all current data** (scores, recommendations, answers). Are you sure you want to continue?
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        // Yes, change set
                        setSelectedSet(pendingSet);
                        resetAll(); // reset scores, recommendations, answers
                        setShowConfirmModal(false);
                        setPendingSet(null);
                      }}
                      className="bg-blue-600 px-3 py-1 rounded hover:bg-blue-500"
                    >
                      Yes, Change
                    </button>
                    <button
                      onClick={() => {
                        // Cancel
                        setShowConfirmModal(false);
                        setPendingSet(null);
                      }}
                      className="bg-gray-700 px-3 py-1 rounded hover:bg-gray-600"
                    >
                      I Stay
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-black border border-blue-500 shadow rounded-lg p-4 mb-6">
              <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                <div>
                  <div className="text-sm text-white">Total Score</div>
                  <div className="text-xl lg:text-3xl font-extrabold text-white">
                    {score} / {maxScore()}
                  </div>
                  <div className="text-sm mt-1 text-white">{levelObj.name}</div>
                </div>

                <div className="flex gap-2">
                  <button onClick={resetAll} className="text-xs lg:text-sm w-32 px-1 py-1 rounded border bg-emerald-500 text-white">🔄 Reset</button>
                  <button onClick={exportJSON} className="text-xs lg:text-sm w-32 px-1 py-1 rounded bg-emerald-500 text-white">
                    🗄️ Export JSON
                  </button>
                </div>
              </div>
            </div>

            <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {baseQuestions.map((q) => (
                <div key={q.id} className="bg-black border border-blue-500 p-4 rounded">
                  <label className="block text-sm font-medium mb-2">{q.label}</label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <label key={n} className={`cursor-pointer ${answers[q.id] === n ? "font-semibold" : ""}`}>
                        <input
                          type="radio"
                          name={q.id}
                          value={n}
                          checked={answers[q.id] === n}
                          onChange={(e) => handleChange(q.id, e.target.value)}
                          className="mr-2"
                        />
                        {n}
                      </label>
                    ))}
                  </div>
                  <div className="text-xs text-white mt-2">Pillar: {q.pillar}</div>
                </div>
              ))}

              <div className="md:col-span-2 flex items-center gap-3 text-white">
                <input
                  type="checkbox"
                  checked={includeHoreca}
                  onChange={(e) => setIncludeHoreca(e.target.checked)}
                />
                Include Horeca questions
              </div>

              {includeHoreca &&
                horecaExtras.map((q) => (
                  <div key={q.id} className="bg-black border border-blue-500 p-4 rounded">
                    <label className="block text-sm font-medium mb-2">{q.label}</label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <label key={n} className={`cursor-pointer ${horecaAnswers[q.id] === n ? "font-semibold" : ""}`}>
                          <input
                            type="radio"
                            name={q.id}
                            value={n}
                            checked={horecaAnswers[q.id] === n}
                            onChange={(e) => handleChange(q.id, e.target.value, true)}
                            className="mr-2"
                          />
                          {n}
                        </label>
                      ))}
                    </div>
                    <div className="text-xs text-white mt-2">Pillar: {q.pillar}</div>
                  </div>
                ))}
            </form>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 bg-white border border-blue-500 p-4 rounded">
                <h3 className="font-semibold mb-2 text-blue-500">Pillar Radar</h3>
                <div className="relative h-72">
                  <Radar
                    data={radarData}
                    options={radarOptions}
                  />
                </div>
              </div>

              <div className="bg-black border border-blue-500 p-4 rounded text-white">
                <h3 className="font-semibold mb-2">Recommendations</h3>
                <div className="font-medium">Stage: {levelObj.name}</div>
                <ul className="list-disc pl-5 mt-2">
                  {recs.map((r, idx) => (
                    <li key={idx}>{r}</li>
                  ))}
                </ul>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2 mt-4 place-items-center">
                  <button
                    onClick={() => window.print()}
                    className="bg-blue-500 text-sm px-3 py-1 rounded border w-full"
                  >
                    🖨️ Print
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={loading || isSaved}
                    className={`text-sm px-3 py-1 rounded border flex justify-center items-center gap-1 w-full ${
                      isSaved ? "bg-gray-500 text-gray-200 cursor-not-allowed" : "bg-blue-500 text-white"
                    }`}
                  >
                    {loading ? "Saving..." : isSaved ? "Saved ✅" : "💾 Save"}
                  </button>

                  <button
                    onClick={handleShare}
                    className="bg-gray-400 text-sm px-3 py-1 rounded border w-full"
                  >
                    📤 Share
                  </button>
                </div>

                {saveMessage && (
                  <div
                    className={`mt-2 p-2 rounded text-sm ${
                      saveStatus === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
                    }`}
                  >
                    {saveMessage}
                  </div>
                )}            
              </div>
            </div>
            <div className="border-t border-blue-400 mt-4">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2 mt-4 place-items-center">
                <button
                  onClick={handleGeneratePrompt}
                  className="bg-yellow-500 text-sm px-3 py-1 rounded border text-black w-full"
                >
                  🤖 Provide AI Prompt
                </button>
                <button
                  onClick={handleAskThinkyAI}
                  className="bg-green-500 text-sm px-3 py-1 rounded border text-white w-full"
                >
                  🧠 Ask Thinky AI
                </button>
                <button
                  onClick={handleCTA}
                  className="bg-purple-500 text-sm px-3 py-1 rounded border text-white w-full"
                >
                  📝 CTA
                </button>
              </div>
              {notification && (
                <div
                  className={`mt-2 p-2 rounded text-sm ${
                    notificationType === "success"
                      ? "bg-green-600 text-white"
                      : notificationType === "error"
                      ? "bg-red-600 text-white"
                      : "bg-blue-600 text-white"
                  }`}
                >
                  {notification}
                </div>
              )}
              {aiPrompt && (
                <div className="items-center justify-center text-center">
                  <textarea
                    className="text-center mt-2 w-full p-2 rounded border border-gray-400 text-white"
                    rows={4}
                    value={aiPrompt}
                    readOnly
                  />
                </div>
              )}    
            </div>

            <div style={{ display: "none" }}>
              {[1, 2, 3, 4, 5].map((setId) => (
                <div key={setId} id={`setSheet${setId}`}>
                  <h2>Self Assessment – Set {setId}</h2>
                  <Radar
                    data={radarData}
                    options={radarOptions}
                  />                  
                </div>
              ))}
            </div>
            <div style={{ display: "none" }}>
              {[1, 2, 3, 4, 5].map((setId) => (
                <div key={setId} id={`pdfSheet${setId}`}>
                  <h2>Self Assessment – Set {setId}</h2>
                  <Radar
                    data={radarData}
                    options={radarOptions}
                  />                  
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="text-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-yellow-200">
                Previous Assessments
              </h2>

              <button
                onClick={printAllHistory}
                className="text-xs w-32 lg:text-sm lg:w-64 px-4 py-2 rounded border border-yellow-400 text-yellow-300 hover:bg-yellow-400 hover:text-black flex items-center justify-center gap-2"
              >
                🖨️ Print ALL History (Set 1–5)
              </button>
            </div>

            {[1, 2, 3, 4, 5].map((setId) => {
              const setHistory = dummyHistory.filter((h) => h.set_id === setId);

              return (
                <div key={setId} className="mb-6">
                  <h3 className="text-md font-semibold text-yellow-300 mb-2 flex justify-between items-center">
                    <span>Set {setId} History</span>

                    <button
                      onClick={() => printHistoryForSet(setId)}
                      className="w-32 px-3 py-1 text-xs rounded border border-yellow-400 text-yellow-300 hover:bg-yellow-400 hover:text-black"
                    >
                      🖨️ Print
                    </button>
                  </h3>

                  <div className="p-4 border border-blue-500 rounded bg-slate-800 overflow-x-auto">
                    <table className="w-full text-sm text-left border-collapse">
                      <thead className="bg-slate-700 text-yellow-300">
                        <tr>
                          <th className="p-2 border">Date & Time</th>
                          <th className="p-2 border">Level</th>
                          <th className="p-2 border">Difficulty</th>
                          <th className="p-2 border">Score</th>
                          <th className="p-2 border">Total</th>
                          <th className="p-2 border">%</th>
                        </tr>
                      </thead>

                      <tbody>
                        {setHistory.map((h) => (
                          <tr key={h.id}>
                            <td className="p-2 border">{h.date}</td>
                            <td className="p-2 border">{h.level}</td>
                            <td className="p-2 border">{h.difficulty_level}</td>
                            <td className="p-2 border">{h.score}</td>
                            <td className="p-2 border">{h.total}</td>
                            <td className="p-2 border text-yellow-300 font-semibold">
                              {h.percent}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === "trend" && (
          <div className="text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
              <div className="md:flex md:flex-col md:gap-1">
                <h2 className="text-lg font-bold text-yellow-200">Trend / Progress</h2>
                <p className="text-gray-300 text-sm">
                  (Performance trend based on % for selected set)
                </p>
              </div>

              <button
                onClick={printAllTrendsAsPDF}
                className="text-xs w-32 lg:text-sm lg:w-48 mt-3 md:mt-0 px-3 py-1 rounded border text-black bg-yellow-400 hover:bg-yellow-300"
              >
                🖨️ Print All Sets
              </button>
            </div>

            {/* Set selection buttons */}
            <div className="flex space-x-2 mb-4">
              {[1, 2, 3, 4, 5].map((setId) => (
                <button
                  key={setId}
                  onClick={() => setSelectedSet(setId)}
                  className={`px-3 py-1 rounded ${
                    selectedSet === setId
                      ? "bg-yellow-500 text-black"
                      : "bg-gray-700 text-white hover:bg-gray-600"
                  }`}
                >
                  Set {setId}
                </button>
              ))}
            </div>

            <div className="p-4 border border-blue-500 rounded bg-slate-800">
              <Line
                ref={lineChartRef}
                id={`trendChart${selectedSet}`}
                data={trendData}
                options={trendOptions}
              />               <button
                onClick={printTrendForSet}
                className="w-48 mb-3 px-3 py-1 rounded border text-black bg-yellow-400 hover:bg-yellow-400"
              >
                🖨️ Print Trend (Set {selectedSet})
              </button>
            </div>
          </div>
        )}
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}